import type { FC, ReactNode } from 'react'

import { Check, RotateCcw } from 'lucide-react'
import { cn } from '@/lib/utils'
import { buttonStyles } from '@/lib/button-styles'
import { WhatsappIcon } from '@/components/generals/whatsapp-icon'
import { whatsappLink } from '@/constants/site'

interface FormSuccessProps {
    title: string
    description: ReactNode
    reference: string | null
    whatsappMessage: string
    onReset?: () => void
    resetLabel?: string
    className?: string
}

export const FormSuccess: FC<FormSuccessProps> = ({
    title,
    description,
    reference,
    whatsappMessage,
    onReset,
    resetLabel = 'Send another request',
    className
}): ReactNode => {
    const message = reference ? `${whatsappMessage} My reference is ${reference}.` : whatsappMessage

    return (
        <div role='status' className={cn('flex flex-col items-start gap-6 animate-in fade-in slide-in-from-bottom-2 duration-500', className)}>
            <span className='inline-flex size-14 items-center justify-center rounded-full bg-brand-orange/15 text-brand-burnt ring-8 ring-brand-orange/5'>
                <Check className='size-7' aria-hidden='true' />
            </span>

            <div className='space-y-3'>
                <h3 className='font-heading text-3xl leading-tight font-semibold text-brand-charcoal sm:text-4xl'>
                    {title}
                </h3>

                <div className='max-w-xl text-[15px] leading-relaxed text-brand-stone'>
                    {description}
                </div>
            </div>

            {reference && (
                <p className='inline-flex items-center gap-2 rounded-full border border-brand-sand bg-brand-cream px-4 py-2 text-sm text-brand-charcoal'>
                    Your reference
                    <span className='font-semibold tracking-wide text-brand-ember'>
                        {reference}
                    </span>
                </p>
            )}

            <div className='flex flex-wrap gap-3'>
                <a
                    href={whatsappLink(message)}
                    target='_blank'
                    rel='noopener noreferrer'
                    className={buttonStyles({ variant: 'dark' })}
                >
                    <WhatsappIcon className='size-4' />
                    Chat with us on WhatsApp
                </a>

                {onReset && (
                    <button type='button' onClick={onReset} className={buttonStyles({ variant: 'outline' })}>
                        <RotateCcw className='size-4' aria-hidden='true' />
                        {resetLabel}
                    </button>
                )}
            </div>
        </div>
    )
}
